import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Save, Loader2, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NavigationBar } from '../components/ui/NavigationBar';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { AlertModal } from '../components/ui/AlertModal';
import { FormLabel } from '../components/ui/FormLabel';

import { useTranslations } from '../i18n';
import { deleteTempFiles } from '../lib/r2';
import { useQueryClient } from '@tanstack/react-query';
import { LOCATION_KEYS, useCars } from '../hooks/useCarData';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { searchAddress, reverseGeocode } from '../lib/nominatim';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { usePreferences } from '../contexts/PreferencesContext';
import { theme } from '../lib/theme';

// Fix leaflet icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function MapEventsHandler({ position, setPosition, onPositionChanged, isReadOnly }: any) {
    const map = useMapEvents({
        click(e) {
            if (isReadOnly) return;
            setPosition([e.latlng.lat, e.latlng.lng]);
            onPositionChanged(e.latlng.lat, e.latlng.lng);
        },
    });

    useEffect(() => {
        if (position) {
            map.setView(position, map.getZoom());
        }
    }, [position, map]);

    return position ? <Marker position={position} /> : null;
}

interface LocationFormProps {
    carId: string;
    locationId?: string;
    onClose: () => void;
    onCreated?: (location: Location) => void;
    isReadOnly?: boolean;
}

export function LocationForm({ carId, locationId, onClose, onCreated, isReadOnly = false }: LocationFormProps) {
    const { user } = useAuth();
    const { slug } = useParams();
    const queryClient = useQueryClient();
    const outletContext = useOutletContext<any>() || {};
    const { t } = useTranslations();
    const { preferences } = usePreferences();
    const { effectiveUserId = user?.id, isAdminView = false } = outletContext;

    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [description, setDescription] = useState('');
    const [latLng, setLatLng] = useState<[number, number] | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const [images, setImages] = useState<Array<{ url: string; thumbnail?: string; comment?: string; sizeBytes?: number }>>([]);
    const [initialImages, setInitialImages] = useState<string>('[]');
    const [saving, setSaving] = useState(false);
    const [initialValues, setInitialValues] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [logEntries, setLogEntries] = useState<any[]>([]);
    const [loadingLogEntries, setLoadingLogEntries] = useState(false);
    const { data: userCars = [] } = useCars(effectiveUserId);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });



    const savedImageUrlsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        loadData();
        if (locationId) {
            loadLogEntries();
        }
    }, [locationId]);

    const loadLogEntries = async () => {
        if (!locationId) return;
        setLoadingLogEntries(true);
        try {
            const { data, error } = await supabase
                .from('log_entries')
                .select('*, expenses(*), log_entry_images(count), log_entry_parts(count), cars(id, name, slug)')
                .eq('location_id', locationId)
                .order('date', { ascending: false });

            if (error) throw error;
            if (data) {
                setLogEntries(data.map(entry => ({
                    ...entry,
                    imageCount: entry.log_entry_images?.[0]?.count || 0,
                    partCount: entry.log_entry_parts?.[0]?.count || 0,
                    car: entry.cars
                })));
            }
        } catch (err) {
            console.error('Error loading log entries:', err);
        } finally {
            setLoadingLogEntries(false);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            if (locationId) {
                const { data, error } = await supabase.from('locations').select('*').eq('id', locationId).single();
                if (error) throw error;

                if (data) {
                    setName(data.name || '');
                    setAddress(data.address || '');
                    setDescription(data.description || '');
                    if (data.latitude && data.longitude) {
                        setLatLng([data.latitude, data.longitude]);
                    }

                    if (data.image_url) {
                        const imgData = [{
                            url: data.image_url,
                            thumbnail: data.image_url_thumb || data.image_url,
                            sizeBytes: data.image_file_size_bytes
                        }];
                        setImages(imgData);
                        setInitialImages(JSON.stringify(imgData));
                        savedImageUrlsRef.current = new Set([data.image_url]);
                    }

                    setInitialValues({
                        name: data.name || '',
                        address: data.address || '',
                        description: data.description || '',
                        lat: data.latitude,
                        lng: data.longitude
                    });
                }
            } else {
                // Find default center (e.g., Riga)
                setLatLng([56.9496, 24.1052]);
                setInitialValues(null);
            }
        } catch (error) {
            console.error('Error loading location:', error);
        } finally {
            setLoading(false);
        }
    };

    const processLocationResult = (res: any) => {
        if (!res) return;

        // Extract POI/Business name
        let poi: string | null = null;
        if (res.address) {
            poi = res.address.amenity ||
                res.address.shop ||
                res.address.office ||
                res.address.tourism ||
                res.address.leisure ||
                res.address.building ||
                res.address.historic ||
                res.address.emergency ||
                res.address.craft;
        }

        // 1. Extract Name (POI) if Name field is empty
        if (!name.trim() && poi) {
            setName(poi);
        }

        // 2. Format Address (Road HouseNumber, rest...)
        if (res.display_name && res.address) {
            const addr = res.address;
            const parts: string[] = [];

            // Build the first part of the address (Street HouseNumber)
            if (addr.road) {
                if (addr.house_number) {
                    parts.push(`${addr.road} ${addr.house_number}`);
                } else {
                    parts.push(addr.road);
                }
            }

            // Get all parts from Nominatim's display_name
            const displayParts = res.display_name.split(',').map((p: string) => p.trim());

            // Identify parts to exclude
            const excludeSet = new Set([
                addr.house_number,
                addr.road,
                addr.postcode,
                poi
            ].filter(Boolean));

            // Add remaining parts that aren't already included or in the exclude set
            displayParts.forEach((p: string) => {
                if (!excludeSet.has(p) && !parts.some(existing => existing.includes(p))) {
                    parts.push(p);
                }
            });

            setAddress(parts.join(', '));
        }
    };

    const handleMapClick = async (lat: number, lng: number) => {
        // Reverse geocode optionally
        const res = await reverseGeocode(lat, lng);
        if (res) {
            processLocationResult(res);
        }
    };

    const performSearch = async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const res = await searchAddress(searchQuery);
            if (res.length > 0) {
                const first = res[0];
                setLatLng([parseFloat(first.lat), parseFloat(first.lon)]);
                processLocationResult(first);
            } else {
                setAlertConfig({ isOpen: true, title: t.common.error, message: (t.locations as any)?.noResults || 'No results found' });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setAlertConfig({ isOpen: true, title: t.common.error, message: (t.locations as any)?.nameRequired || 'Name is required' });
            return;
        }

        setSaving(true);
        try {
            let imgObj: any = {};
            if (images.length > 0) {
                imgObj = {
                    image_url: images[0].url,
                    image_url_thumb: images[0].thumbnail || images[0].url,
                    image_file_size_bytes: images[0].sizeBytes || 0,
                    image_thumb_file_size_bytes: 0
                };
            } else {
                imgObj = {
                    image_url: null,
                    image_url_thumb: null,
                    image_file_size_bytes: 0,
                    image_thumb_file_size_bytes: 0
                };
            }

            const locationData = {
                car_id: carId, // wait, there is no car_id on location. It is user_id.
                user_id: effectiveUserId,
                name: name.trim(),
                address: address.trim(),
                description: description.trim(),
                latitude: latLng ? latLng[0] : null,
                longitude: latLng ? latLng[1] : null,
                is_temporary: false,
                ...imgObj
            };

            // Fix car_id which is invalid
            delete (locationData as any).car_id;

            if (locationId) {
                // Do not update user_id on update
                delete (locationData as any).user_id;

                const { error } = await supabase
                    .from('locations')
                    .update(locationData)
                    .eq('id', locationId);
                if (error) throw error;
            } else {
                const { data, error } = await supabase
                    .from('locations')
                    .insert(locationData)
                    .select()
                    .single();
                if (error) throw error;

                if (onCreated && data) {
                    onCreated(data);
                }
            }

            queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.list(carId) });

            onClose();
        } catch (error: any) {
            console.error('Error saving location:', error);
            let errorMessage = (t.locations as any)?.saveFailed || 'Error saving location';
            if (error.code === '23514') {
                errorMessage = t.common.mandatoryFieldsError;
            }
            setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCall = async () => {
        if (!locationId) return;

        setSaving(true);
        try {
            const { error } = await supabase.from('locations').delete().eq('id', locationId);
            if (error) throw error;

            queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.list(carId) });
            onClose();
        } catch (error) {
            console.error('Error deleting location:', error);
            setAlertConfig({ isOpen: true, title: t.common.error, message: (t.locations as any)?.deleteFailed || 'Error deleting location' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        setShowDeleteModal(true);
    };

    const hasChanges = initialValues ? (
        name !== initialValues.name ||
        address !== initialValues.address ||
        description !== initialValues.description ||
        (latLng ? latLng[0] : null) !== initialValues.lat ||
        (latLng ? latLng[1] : null) !== initialValues.lng ||
        JSON.stringify(images) !== initialImages
    ) : (
        name.trim() !== '' ||
        address.trim() !== '' ||
        description.trim() !== '' ||
        images.length > 0
    );

    const handleBack = async () => {
        if (hasChanges && !isReadOnly && !saving) {
            setShowConfirmModal(true);
        } else {
            const unsavedUrls = images
                .map(img => img.url)
                .filter(url => url && !savedImageUrlsRef.current.has(url));
            if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
            onClose();
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center font-primary bg-gradient-theme">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex flex-col h-full overflow-hidden z-50 bg-gradient-theme">
            <NavigationBar
                title={isReadOnly ? ((t.locations as any)?.viewLocation || 'View location') : (locationId ? ((t.locations as any)?.editLocation || 'Edit location') : ((t.locations as any)?.newLocation || 'Jauna lokācija'))}
                onBack={handleBack}
                fixed={false}
                rightAction={(!isReadOnly && hasChanges) ? {
                    icon: saving ? Loader2 : Save,
                    onClick: handleSave,
                    disabled: saving,
                    className: saving ? 'animate-spin' : ''
                } : undefined}
            />

            <div className="flex-1 overflow-y-auto">
                <div className="p-4 space-y-6 pb-20 max-w-2xl mx-auto">

                    <div className="space-y-6">
                        {/* 1. Map & Address */}
                        <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4 relative">
                            <div className="flex flex-col mb-4">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{(t.locations as any)?.mapLabel || 'Atrašanās vieta'}</label>
                                {!isReadOnly && (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder={(t.locations as any)?.searchPlaceholder || 'Meklēt adresi...'}
                                            className="flex-1 px-3 py-2 bg-white dark:bg-slate-900/50 border border-gray-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                        />
                                        <button type="button" onClick={performSearch} disabled={isSearching} className="bg-blue-600 dark:bg-blue-500 text-white p-3 rounded-xl flex items-center justify-center">
                                            {isSearching ? <Loader2 size={24} className="animate-spin" /> : <Search size={24} />}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="w-full h-48 sm:h-64 rounded-xl overflow-hidden mb-4 border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800">
                                {latLng && (
                                    <MapContainer center={latLng} zoom={13} style={{ height: '100%', width: '100%' }}>
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                                        <MapEventsHandler 
                                            position={latLng} 
                                            setPosition={setLatLng} 
                                            onPositionChanged={handleMapClick} 
                                            isReadOnly={isReadOnly}
                                        />
                                    </MapContainer>
                                )}
                            </div>

                            <div className="flex flex-col gap-1.5 pt-4">
                                <FormLabel className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {(t.locations as any)?.addressLabel || 'Address'}
                                </FormLabel>
                                <input
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder={(t.locations as any)?.addressPlaceholder || 'Optional address'}
                                    readOnly={isReadOnly}
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-900/50 border border-gray-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <FormLabel 
                                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                                isFilled={!!name.trim()}
                            >
                                {`${(t.locations as any)?.nameLabel || 'Name'} *`}
                            </FormLabel>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={(t.locations as any)?.namePlaceholder || 'e.g. My Garage'}
                                readOnly={isReadOnly}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900/50 border border-gray-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                required
                            />
                        </div>

                        {/* 3. Description */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {(t.locations as any)?.descriptionLabel || 'Description'}
                            </FormLabel>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={(t.locations as any)?.descriptionPlaceholder || 'Optional notes'}
                                readOnly={isReadOnly}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900/50 border border-gray-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 min-h-[80px]"
                            />
                        </div>
                    </div>

                    {locationId && !isReadOnly && (
                        <div className="pt-4">
                            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="w-full text-left p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center justify-between"
                                >
                                    {(t.locations as any)?.deleteLocation || 'Delete location'}
                                </button>
                            </div>
                        </div>
                    )}

                    {locationId && (
                        <div className="pt-8 space-y-6 px-1">
                            <h4 className={`${theme.typography.sectionTitle}`}>
                                {t.common.history}
                            </h4>

                            {loadingLogEntries ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-4" />
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* Current Car Section */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                            <div className="p-1 px-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-md uppercase tracking-wider">
                                                {t.common.currentCar}
                                            </div>
                                            <span className="text-sm font-semibold">{userCars.find(c => c.id === carId)?.name || ''}</span>
                                        </div>

                                        {logEntries.filter(e => e.car_id === carId).length === 0 ? (
                                            <div className="bg-white dark:bg-slate-900/40 border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-6 text-center">
                                                <p className="text-sm text-gray-400 italic">
                                                    {(t.locations as any)?.noEntriesThisCar || 'Šim auto nav izmantota šī lokācija'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:divide-white/30 border-white/5 rounded-2xl divide-y divide-gray-100 dark:divide-white/5 shadow-sm overflow-hidden">
                                                {logEntries.filter(e => e.car_id === carId).map(entry => (
                                                    <LogEntryItem
                                                        key={entry.id}
                                                        entry={entry}
                                                        slug={entry.car?.slug || slug || ''}
                                                        isAdminView={isAdminView}
                                                        effectivePreferences={preferences}
                                                        forceShowYear={true}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Other Cars Sections */}
                                    {userCars.filter(c => c.id !== carId).map(car => {
                                        const carEntries = logEntries.filter(e => e.car_id === car.id);
                                        if (carEntries.length === 0) return null;

                                        return (
                                            <div key={car.id} className="space-y-3">
                                                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                                    <div className="p-1 px-2 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 text-xs font-bold rounded-md uppercase tracking-wider">
                                                        {t.common.car}
                                                    </div>
                                                    <span className="text-sm font-semibold">{car.name}</span>
                                                </div>
                                                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/5 rounded-2xl divide-y divide-gray-100 dark:divide-white/5 shadow-sm overflow-hidden">
                                                    {carEntries.map(entry => (
                                                        <LogEntryItem
                                                            key={entry.id}
                                                            entry={entry}
                                                            slug={car.slug}
                                                            isAdminView={isAdminView}
                                                            effectivePreferences={preferences}
                                                            openInNewTab={true}
                                                            forceShowYear={true}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="h-[40vh]" />
            </div>



            <ConfirmModal
                isOpen={showDeleteModal}
                title={(t.locations as any)?.deleteLocation || 'Delete location'}
                message={(t.locations as any)?.deleteConfirm || 'Are you sure?'}
                confirmLabel={t.common.delete}
                cancelLabel={t.common.cancel}
                onConfirm={handleDeleteCall}
                onCancel={() => setShowDeleteModal(false)}
                onClose={() => setShowDeleteModal(false)}
                isDestructive={true}
            />

            <ConfirmModal
                isOpen={showConfirmModal}
                title={t.common.unsavedChangesTitle}
                message={t.common.unsavedChangesMessage}
                confirmLabel={t.common.save}
                cancelLabel={t.common.dontSave}
                onConfirm={() => {
                    handleSave();
                    setShowConfirmModal(false);
                }}
                onCancel={() => {
                    setShowConfirmModal(false);
                    const unsavedUrls = images.map(img => img.url).filter(url => url && !savedImageUrlsRef.current.has(url));
                    if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
                    onClose();
                }}
                onClose={() => setShowConfirmModal(false)}
                isDestructive={false}
            />

            <AlertModal
                isOpen={alertConfig.isOpen}
                title={alertConfig.title}
                message={alertConfig.message}
                confirmLabel={t.common.ok}
                onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
            />
        </div >
    );
}
